import { Trans, useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, InputGroup } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { FaTimes } from "react-icons/fa";
import "react-bootstrap-typeahead/css/Typeahead.css";
import studyClient, {
  type RecordingSearchItem,
} from "@shared/services/studyClient";
import { formatRecordingSearchLabel } from "@shared/utils/asyncRecordingSelectStorage";
import { getNonAuthSearchInputProps } from "@shared/utils/nonAuthPasswordFields";
import "./AsyncStudySelect.css";

interface StudyOption {
  id: string;
  label: string;
  study_id: string;
  recording_id?: string;
  study_date: string;
  patient_name: string;
  mrn: string;
  study_type?: string;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  /** Optional richer selection callback (patient/MRN/study metadata). */
  onOptionChange?: (option: StudyOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  valueKind?: "recording" | "study";
  clearLabel?: string;
}

export type AsyncStudySelectOption = StudyOption;

function mapSearchItem(
  item: RecordingSearchItem,
  valueKind: "recording" | "study",
): StudyOption {
  const studyDate = item.studyDate
    ? new Date(item.studyDate).toLocaleDateString()
    : "—";
  const label = formatRecordingSearchLabel({
    patientName: item.patientName,
    mrn: item.mrn,
    examId: item.examId,
    studyDate: item.studyDate,
  });

  return {
    id: valueKind === "study" ? item.studyId : item.recordingId,
    study_id: item.studyId,
    recording_id: item.recordingId,
    label,
    study_date: studyDate,
    patient_name: item.patientName,
    mrn: item.mrn,
    study_type: item.processingStatus || undefined,
  };
}

function shouldSearch(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  if (/^\d+$/.test(trimmed)) {
    return trimmed.length >= 1;
  }
  return trimmed.length >= 2;
}

const AsyncStudySelect: React.FC<Props> = ({
  value,
  onChange,
  onOptionChange,
  placeholder,
  disabled = false,
  valueKind = "recording",
  clearLabel,
}) => {
  const { t } = useTranslation("features");
  const [options, setOptions] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hint = rootRef.current?.querySelector(".rbt-input-hint");
    if (hint) {
      hint.setAttribute("aria-hidden", "true");
      hint.setAttribute("tabindex", "-1");
    }
  });

  const runSearch = useCallback(async (searchQuery: string) => {
    if (!shouldSearch(searchQuery)) {
      setOptions([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    try {
      const response = await studyClient.searchRecordings({
        q: searchQuery.trim() || undefined,
        limit: 20,
      });
      if (controller.signal.aborted) return;

      const payload =
        (response.data as { data?: { items?: RecordingSearchItem[] } })?.data ||
        (response.data as { items?: RecordingSearchItem[] });
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setOptions(items.map((item) => mapSearchItem(item, valueKind)));
    } catch {
      if (!controller.signal.aborted) {
        setOptions([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [valueKind]);

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      void runSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      abortRef.current?.abort();
    };
  }, [query, runSearch]);

  const selected = value
    ? options
        .filter((option) =>
          valueKind === "study"
            ? option.study_id === value || option.id === value
            : option.recording_id === value || option.id === value,
        )
        .slice(0, 1)
    : [];

  const resolvedClearLabel =
    clearLabel ||
    (valueKind === "study" ? "Clear selected study" : "Clear selected recording");

  return (
    <div className="async-study-select" ref={rootRef}>
    <InputGroup className="async-study-select__group">
      <div className="async-study-select__typeahead">
        <Typeahead
          id="async-study-search-query"
          options={options}
          selected={selected}
          onInputChange={(text) => setQuery(text)}
          onChange={(items) => {
            const item = items[0] as StudyOption | undefined;
            onChange(
              valueKind === "study"
                ? (item?.study_id ?? "")
                : (item?.recording_id ?? item?.id ?? ""),
            );
            onOptionChange?.(item ?? null);
          }}
          labelKey="label"
          filterBy={() => true}
          inputProps={{
            ...getNonAuthSearchInputProps(
              valueKind === "study" ? "study-select" : "recording-select",
            ),
            "aria-label":
              placeholder ||
              (valueKind === "study"
                ? "Search studies by patient name, MRN, or date"
                : "Search recordings by patient name, MRN, or date"),
          }}
          placeholder={
            placeholder ||
            t("ui.patientSearch.studyPlaceholder", {
              defaultValue: "Search by patient name, MRN, or date...",
            })
          }
          isLoading={loading}
          disabled={disabled}
          minLength={0}
          renderMenuItemChildren={(option) => {
            const optionItem = option as StudyOption;
            return (
              <div>
                <strong>{optionItem.patient_name}</strong>
                <div className="small text-muted">
                  <Trans ns="features" i18nKey="ui.copy.4f63f4044a5f" />
                  {optionItem.mrn} · {optionItem.study_date}
                  {optionItem.study_type ? ` · ${optionItem.study_type}` : ""}
                </div>
              </div>
            );
          }}
        />
      </div>
      {value && (
        <Button
          type="button"
          variant="outline-secondary"
          className="async-study-select__clear"
          aria-label={resolvedClearLabel}
          title={resolvedClearLabel}
          disabled={disabled}
          onClick={() => {
            onChange("");
            onOptionChange?.(null);
            setQuery("");
          }}
        >
          <FaTimes aria-hidden="true" />
        </Button>
      )}
    </InputGroup>
    </div>
  );
};

export default AsyncStudySelect;
